<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LeadsAPI from 'dashboard/api/leads';
import { formatLeadCurrency } from '../helper';
import DashboardWidget from './DashboardWidget.vue';

const props = defineProps({
  pipelineId: { type: [Number, String], default: null },
  currency: { type: String, default: 'USD' },
  assigneeId: { type: [Number, String], default: '' },
  period: { type: String, default: 'today' }, // controlado desde el header
});
// 'navigate-agenda' → { preset } ; 'navigate-board' → { ...filters }
const emit = defineEmits(['navigate-agenda', 'navigate-board']);

const { t } = useI18n();
const data = ref(null);
const showForecast = ref(false);

const fmt = value => formatLeadCurrency(value, props.currency);

const load = async () => {
  if (!props.pipelineId) return;
  try {
    const { data: res } = await LeadsAPI.dashboard({
      pipeline_id: props.pipelineId,
      period: props.period,
      assignee_id: props.assigneeId,
    });
    data.value = res.payload;
  } catch {
    data.value = null;
  }
};

watch(() => [props.pipelineId, props.assigneeId, props.period], load, {
  immediate: true,
});
defineExpose({ reload: load });
</script>

<template>
  <div class="flex flex-col gap-2">
    <!-- Móvil: tira horizontal scrolleable de chips. Desktop: fila que envuelve. -->
    <div
      v-if="data"
      class="flex gap-2 overflow-x-auto pb-1 md:flex-wrap md:overflow-x-visible md:pb-0"
    >
      <DashboardWidget
        :label="t('LEADS.WIDGETS.TASKS')"
        :value="data.tasks"
        icon="i-lucide-calendar-check"
        tone="blue"
        clickable
        @select="emit('navigate-agenda', { preset: 'today' })"
      />
      <DashboardWidget
        :label="t('LEADS.WIDGETS.OVERDUE')"
        :value="data.overdue"
        icon="i-lucide-alarm-clock"
        tone="red"
        :hint="t('LEADS.WIDGETS.CURRENT')"
        clickable
        @select="emit('navigate-agenda', { preset: 'overdue' })"
      />
      <DashboardWidget
        :label="t('LEADS.WIDGETS.WITHOUT_TASK')"
        :value="data.without_task"
        icon="i-lucide-circle-dashed"
        tone="amber"
        :hint="t('LEADS.WIDGETS.CURRENT')"
        clickable
        @select="emit('navigate-board', { has_pending_task: 'false' })"
      />
      <DashboardWidget
        :label="t('LEADS.WIDGETS.NEW')"
        :value="data.new_leads"
        icon="i-lucide-sparkles"
        tone="indigo"
        clickable
        @select="emit('navigate-board', { period })"
      />
      <DashboardWidget
        :label="t('LEADS.WIDGETS.WON')"
        :value="fmt(data.won.value)"
        icon="i-lucide-trophy"
        tone="green"
        clickable
        @select="emit('navigate-board', { status: 'won', period })"
      />
      <DashboardWidget
        :label="t('LEADS.WIDGETS.FORECAST')"
        :value="fmt(data.forecast.value)"
        icon="i-lucide-trending-up"
        tone="indigo"
        :hint="t('LEADS.WIDGETS.CURRENT')"
        clickable
        @select="showForecast = !showForecast"
      />
    </div>

    <!-- Desglose del forecast: panel full-width debajo (sirve móvil y desktop). -->
    <div
      v-if="data && showForecast"
      class="rounded-lg border border-n-weak bg-n-solid-1 p-3 text-xs"
    >
      <p class="mb-2 font-semibold">
        {{ t('LEADS.WIDGETS.FORECAST_BREAKDOWN') }}
      </p>
      <div
        v-for="b in data.forecast.breakdown"
        :key="b.stage_id"
        class="flex justify-between gap-2 py-0.5"
      >
        <span class="truncate text-n-slate-11">
          {{ b.stage_name }} ({{ Math.round(b.rate * 100) }}%)
        </span>
        <span class="tabular-nums">{{ fmt(b.contribution) }}</span>
      </div>
      <p v-if="!data.forecast.breakdown.length" class="text-n-slate-10">
        {{ t('LEADS.WIDGETS.FORECAST_EMPTY') }}
      </p>
    </div>
  </div>
</template>
